import React, {useEffect, useState} from 'react';
import {X} from 'lucide-react';
import {useSupportedLanguages} from "../../../base/hooks/useSupportedLanguages";
import {Select, SelectOption} from "../../../base/inputs/select/Select";

export interface SessionData {
  name: string;
  originalLanguage: string;
  translationLanguages: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (sessionData: SessionData) => void;
  initialData?: SessionData;
}

export const SessionModal: React.FC<Props> = ({
  isOpen, 
  onClose, 
  onSubmit,
  initialData 
}) => {
  const [name, setName] = useState('');
  const [originalLanguage, setOriginalLanguage] = useState('');
  const [translationLanguages, setTranslationLanguages] = useState<string[]>([]);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setOriginalLanguage(initialData.originalLanguage);
      setTranslationLanguages(initialData.translationLanguages);
    } else {
      setName('');
      setOriginalLanguage('');
      setTranslationLanguages([]);
    }
  }, [initialData, isOpen]);

  const { supportedLanguages } = useSupportedLanguages();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, originalLanguage, translationLanguages });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 m-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {initialData ? 'Edit Session' : 'Create New Session'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Session Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter session name"
              required
            />
          </div>

          <Select
              label="Source Language"
              placeholder="Select source language"
              options={supportedLanguages}
              defaultValue={initialData?.originalLanguage}
              onChange={(language) => setOriginalLanguage((language as SelectOption).value)}
          />

          <Select
              multiselect
              label="Target Languages"
              placeholder="All Languages"
              options={supportedLanguages}
              defaultValue={initialData?.translationLanguages}
              onChange={(language) => setTranslationLanguages((language as unknown as SelectOption[]).map(l => l.value))}
          />

          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              disabled={!name || !originalLanguage}
            >
              {initialData ? 'Save Changes' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
